import { useState } from 'react';
import { useAppSelector } from 'app/hooks';
import {
  selectIsEligibleForUpsellBanner,
  selectIsProprietaryUser,
} from 'services/ctp/ctpSelectors';
import { namespacedKey } from 'app/storageNamespace';
import DiscoverUpdateCc from './DiscoverUpdateCc';
import Upsell from './Upsell';

/**
 * Angular's custom-notification tier — client-synthesised banners that share the
 * critical-notification slot with the BE-supplied list. Angular holds them in an
 * array and renders `customNotifications[0]`
 * (`critical-notifications.component.html:11-38`), so at most one ever shows.
 *
 * Both of Angular's members have a component here. Which one a caller uses is
 * its own choice: `<CriticalNotification>` synthesises the Discover banner into
 * its own `displayNotifications` list (as PI-11905 shipped it) and therefore
 * asks this tier for the upsell only — see `includeDiscoverUpdateCc`.
 */
export const CUSTOM_NOTIFICATION = {
  upsell: 'upsell',
  discoverUpdateCc: 'discover_update_cc',
} as const;

export type CustomNotificationName =
  (typeof CUSTOM_NOTIFICATION)[keyof typeof CUSTOM_NOTIFICATION];

/* Angular parity — `critical-notifications.component.ts:108`. Dismissal survives
   a reload but not a new tab, so the banner comes back next session. */
const UPSELL_DISMISSED_KEY = namespacedKey('dismissUpsellBanner');

type Options = {
  /**
   * Whether the slot already shows something — the BE list, plus any banner the
   * caller renders itself. Angular renders the custom tier only as the `else`
   * of the server list (`critical-notifications.component.html:2`), so the
   * upsell stands down rather than displacing anything.
   */
  isSlotOccupied: boolean;
  /**
   * Pass `false` when the caller renders the Discover banner itself, so it is
   * not offered twice. Defaults to `true` so a new caller gets the whole tier.
   */
  includeDiscoverUpdateCc?: boolean;
};

/**
 * Decides which custom banner — if any — the slot should show, and hands back the
 * dismissal for it.
 *
 * Lives here rather than in the caller so the tier's eligibility, priority and
 * dismissal stay in one place, and the caller only renders what it is handed.
 *
 * Discover is tested ahead of the upsell: it warns about a pending service
 * interruption, so an upsell must never take the slot from it.
 */
export function useCustomNotification({
  isSlotOccupied,
  includeDiscoverUpdateCc = true,
}: Options) {
  const isUpsellEligible = useAppSelector(selectIsEligibleForUpsellBanner);
  const isDiscoverEligible = useAppSelector(selectIsProprietaryUser);
  const [isUpsellDismissed, setIsUpsellDismissed] = useState(
    () => sessionStorage.getItem(UPSELL_DISMISSED_KEY) !== null,
  );
  /* Angular parity — `critical-notifications.component.html:1-11`.
   * The whole custom-notification tier sits inside the `else` of the
   * BE-notification `*ngIf`, so BOTH Discover CC and Upsell must
   * stand down when the slot is occupied by a BE notification.
   * Previously only Upsell was gated on `!isSlotOccupied`, so the
   * Discover banner still displaced the BE list — now both are. */
  let activeNotification: CustomNotificationName | null = null;
  if (!isSlotOccupied) {
    if (includeDiscoverUpdateCc && isDiscoverEligible) {
      activeNotification = CUSTOM_NOTIFICATION.discoverUpdateCc;
    } else if (isUpsellEligible && !isUpsellDismissed) {
      activeNotification = CUSTOM_NOTIFICATION.upsell;
    }
  }

  /* Discover has no close control (Angular `.discover-cc`), so dismissal
     only applies to the upsell. */
  const dismissCustomNotification = () => {
    sessionStorage.setItem(UPSELL_DISMISSED_KEY, 'true');
    setIsUpsellDismissed(true);
  };

  return { activeNotification, dismissCustomNotification };
}

type Props = {
  /** `null` renders nothing — mirrors Angular's empty `customNotifications`. */
  name: CustomNotificationName | null;
  // eslint-disable-next-line no-unused-vars
  onDismiss: () => void;
};

function CustomNotification({ name, onDismiss }: Props) {
  if (name === CUSTOM_NOTIFICATION.upsell) {
    return <Upsell onDismiss={onDismiss} />;
  }
  if (name === CUSTOM_NOTIFICATION.discoverUpdateCc) {
    return <DiscoverUpdateCc />;
  }
  return null;
}

export default CustomNotification;
